import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_MODULES_FALLBACK,
  resolveLandingModules,
  planIncludesModule,
} from "../../../constants/planMarketing";
import s from "./homeLanding.module.css";

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

const PLAN_FOR_WHOM = {
  start: "Один владелец, без кабинетов и брендинга — только учёт группы.",
  club: "Команда и клиенты в приложении. Брендинг и соцсеть — модулями или в «Про».",
  scale: "Крупная секция: брендинг и соцсеть уже внутри, выше лимиты.",
  pro: "Крупная секция: брендинг и соцсеть уже внутри, выше лимиты.",
  basic: "Если ведёте группу сами.",
  business: "Максимум возможностей и лимитов для большой секции.",
};

export default function LandingPricingBuilder({
  plans,
  plansLoading,
  featuredCode,
  onTryClick,
}) {
  const [modules, setModules] = useState(LANDING_MODULES_FALLBACK);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedModules, setSelectedModules] = useState(() => new Set());
  const [highlightIncluded, setHighlightIncluded] = useState(false);
  const modulesRef = useRef(null);
  const highlightTimer = useRef(null);

  useEffect(() => {
    // Цены/тексты модулей на лендинге — канон; API не перетирает отображение.
    platformAPI
      .getPublicModules()
      .then((r) => {
        setModules(resolveLandingModules(r?.modules));
      })
      .catch(() => setModules(LANDING_MODULES_FALLBACK));
  }, []);

  useEffect(() => {
    if (!plans.length) return;
    const featured =
      plans.find((p) => p.code === featuredCode) ||
      plans.find((p) => p.badge) ||
      plans.find((p) => p.code === "club") ||
      plans[0];
    setSelectedPlanId((prev) => {
      if (prev && plans.some((p) => String(p.id) === String(prev))) return prev;
      return featured?.id ?? plans[0].id;
    });
  }, [plans, featuredCode]);

  const selectedPlan = useMemo(
    () => plans.find((p) => String(p.id) === String(selectedPlanId)) || null,
    [plans, selectedPlanId],
  );

  const selectPlan = (planId) => {
    setSelectedPlanId(planId);
    setHighlightIncluded(true);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightIncluded(false), 1600);
    // чуть позже, чтобы DOM модулей обновил «В тарифе»
    requestAnimationFrame(() => {
      modulesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    [],
  );

  const moduleRows = useMemo(() => {
    const rows = modules.map((mod) => {
      const included = selectedPlan
        ? planIncludesModule(selectedPlan, mod.code)
        : false;
      return {
        ...mod,
        included,
        active: included || selectedModules.has(mod.code),
      };
    });
    // Включённые в тариф — сверху, чтобы сразу было видно
    return rows.sort((a, b) => {
      if (a.included !== b.included) return a.included ? -1 : 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [modules, selectedPlan, selectedModules]);

  const addonsMonthly = useMemo(() => {
    let sum = 0;
    for (const mod of moduleRows) {
      if (mod.included || !selectedModules.has(mod.code)) continue;
      if (mod.pricing === "once") continue;
      sum += Number(mod.price_rub) || 0;
    }
    return sum;
  }, [moduleRows, selectedModules]);

  const addonsOnce = useMemo(() => {
    let sum = 0;
    for (const mod of moduleRows) {
      if (mod.included || !selectedModules.has(mod.code)) continue;
      if (mod.pricing !== "once") continue;
      sum += Number(mod.price_rub) || 0;
    }
    return sum;
  }, [moduleRows, selectedModules]);

  const planMonthly = Number(selectedPlan?.monthly_price_rub) || 0;
  const totalMonthly = planMonthly + addonsMonthly;

  const toggleModule = (code, included) => {
    if (included) return;
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // Демо всегда на максимальном тарифе (backend: applyTrialBusinessPlan).
  // Выбор карточки на лендинге — только калькулятор, не план пробного периода.
  const registerTo = "/registraciya";

  return (
    <>
      {plansLoading ? (
        <p className={s.pricingLoading}>Загрузка тарифов…</p>
      ) : (
        <div className={s.pricing}>
          {plans.map((plan) => {
            const selected = String(plan.id) === String(selectedPlanId);
            const featured = plan.code === featuredCode;
            const whom = PLAN_FOR_WHOM[plan.code];
            return (
              <button
                key={plan.id}
                type="button"
                className={`${s.priceCard} ${s.priceCardSelectable} ${
                  featured ? s.priceCardFeatured : ""
                } ${selected ? s.priceCardSelected : ""}`}
                onClick={() => selectPlan(plan.id)}
                aria-pressed={selected}
              >
                {plan.badge ? (
                  <span className={s.badge}>{plan.badge}</span>
                ) : null}
                <h3 className={s.priceName}>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePositioning}>
                    {PLAN_POSITIONING[plan.code]}
                  </p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small> / мес за клуб</small>
                </p>
                {plan.yearly_price_rub ? (
                  <p className={s.priceYearly}>
                    или {formatRub(plan.yearly_price_rub)} ₽ / год
                  </p>
                ) : null}
                {whom ? <p className={s.priceForWhom}>{whom}</p> : null}
                <ul className={s.priceHighlights}>
                  {buildLandingPriceHighlights(plan.code).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <span className={s.priceSelectHint}>
                  {selected ? "Выбран" : "Выбрать"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!plansLoading && moduleRows.length ? (
        <div className={s.pricingModules} ref={modulesRef}>
          <div className={s.pricingModulesHead}>
            <h3 className={s.pricingModulesTitle}>Модули</h3>
          </div>
          <div className={s.pricingModuleGrid}>
            {moduleRows.map((mod) => (
              <button
                key={mod.code}
                type="button"
                className={`${s.pricingModuleCard} ${
                  mod.active ? s.pricingModuleCardOn : ""
                } ${mod.included ? s.pricingModuleCardIncluded : ""} ${
                  highlightIncluded && mod.included
                    ? s.pricingModuleCardFlash
                    : ""
                }`}
                onClick={() => toggleModule(mod.code, mod.included)}
                disabled={mod.included}
                aria-pressed={mod.active}
              >
                <span className={s.pricingModuleTop}>
                  <strong className={s.pricingModuleName}>{mod.name}</strong>
                  <span className={s.pricingModuleBadge}>
                    {mod.included
                      ? "В тарифе"
                      : mod.active
                        ? "В сумме"
                        : "+ Добавить"}
                  </span>
                </span>
                {mod.description ? (
                  <span className={s.pricingModuleDesc}>{mod.description}</span>
                ) : null}
                <span className={s.pricingModulePrice}>
                  {mod.included
                    ? "Уже входит — доплачивать не нужно"
                    : mod.pricing === "once"
                      ? `${formatRub(mod.price_rub)} ₽ разово`
                      : `+${formatRub(mod.price_rub)} ₽ / мес`}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {!plansLoading && selectedPlan ? (
        <div className={s.pricingSummary}>
          <div className={s.pricingSummaryText}>
            <p className={s.pricingSummaryLabel}>Итого в месяц</p>
            <p className={s.pricingSummaryAmount}>
              {formatRub(totalMonthly)} ₽
              <small> / мес за клуб</small>
            </p>
            <p className={s.pricingSummaryMeta}>
              Тариф «{selectedPlan.name}» — {formatRub(planMonthly)} ₽
              {addonsMonthly > 0
                ? ` · модули +${formatRub(addonsMonthly)} ₽`
                : " · без доп. модулей"}
              {addonsOnce > 0
                ? ` · разово ещё ${formatRub(addonsOnce)} ₽`
                : ""}
            </p>
            <p className={s.pricingSummaryTrial}>
              Сейчас платить не нужно: {TRIAL_DAYS} дней полного доступа при
              регистрации.
            </p>
          </div>
          <Link
            to={registerTo}
            className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
            onClick={onTryClick}
          >
            Попробовать бесплатно
          </Link>
        </div>
      ) : null}
    </>
  );
}
